import React, { useState, useEffect, useRef } from "react";
import { MapContainer, LayerGroup, GeoJSON, Polygon, Marker, TileLayer } from "react-leaflet";
import { ZoomControl as LeafletZoomControl } from "leaflet";
import { ZoomControl } from "../ZoomControl";
import { LiveReportImageLayer } from "../../../Map/components/ImageLayer";
import { MapLayoutLayer } from "../../../Map/components";
import { LiveReportMapWrapper } from "./styles";
import { useSelector, useDispatch } from "react-redux";
import { siftActions, getBounds } from "../../../Redux";

const zoomGranularity = 0.25;
const basePolyStates = { Boundary: true, Exclusions: true, Inactive: true, Layout: true };

const LiveReportMap = ({ mapName, showToggles, addMapRefs }) => {
  const dispatch = useDispatch();
  const features = useSelector((state) => state.inputs.site_features);
  const layout = useSelector((state) => state.sift.ioManager.outputs.layout);
  const roads = layout?.roads;
  const layer_toggle = useSelector((state) => state.sift.ioManager.uiState.layer_toggle);
  const mapCenter = useSelector((state) => state.inputs.map_center);

  const [position, setPosition] = useState(mapCenter);
  const [zoom, setZoom] = useState(mapName === "vicinityMap" ? 11 : 16);
  const [showBoundary, setShowBoundary] = useState(true);
  const [showExclusion, setShowExclusion] = useState(true);
  const [showInactive, setShowInactive] = useState(true);
  const [show_racks, set_show_racks] = useState(true);
  const [show_roads, set_show_roads] = useState(true);
  const [show_inverters, set_show_inverters] = useState(true);
  const [map, setMap] = useState(null);

  const markerRef = useRef();
  const [showControls, setShowControls] = useState(false);

  useEffect(() => {
    if (!map) return;
    addMapRefs(`${mapName}`, {
      height: map._size.y,
      width: map._size.x,
      className: mapName,
    });
    if (mapName !== "vicinityMap") {
      setTimeout(() => {
        handleLiveMapZoomExtents();
      }, 500);
    }
  }, [map]);

  const handleMarkerDrag = () => {
    if (markerRef?.current) {
      const pos = markerRef.current.getLatLng();
      setPosition(pos);
      dispatch(siftActions.updateReportData("location", [pos.lat, pos.lng]));
    }
  };

  const handleLiveMapZoomExtents = (zoomLevel) => {
    if (!features || Object.values(features).length === 0 || !map) return;
    const bounds = getBounds(Object.values(features));
    const leafletBounds = [
      [bounds[1], bounds[0]],
      [bounds[3], bounds[2]],
    ];
    map.fitBounds(leafletBounds, zoomLevel ? { maxZoom: zoomLevel } : {});
  };

  return (
    <LiveReportMapWrapper onMouseEnter={() => setShowControls(true)} onMouseLeave={() => setShowControls(false)} small={true}>
      {showToggles ? (
        <section className="map-toggles">
          <h3>Layout Map</h3>
          <section className="map-toggle-buttons">
            {[
              ["Show Boundaries", showBoundary, setShowBoundary],
              ["Show Exclusions", showExclusion, setShowExclusion],
              ["Show Inactive", showInactive, setShowInactive],
              ["Show Racks", show_racks, set_show_racks],
              ["Show Roads", show_roads, set_show_roads],
              ["Show Inverters", show_inverters, set_show_inverters],
            ].map(([label, value, setter], idx) => (
              <div className="toggle" key={idx}>
                <p>{label}</p>
                <label className="switch">
                  <input type="checkbox" checked={value} onChange={() => setter(!value)} />
                  <span className="slider round"></span>
                </label>
              </div>
            ))}
          </section>
        </section>
      ) : (
        <section className="map-title">
          <h3>Vicinity Map</h3>
        </section>
      )}

      <section className="map-area">
        <MapContainer
          whenReady={(e) => setMap(e.target)}
          id={mapName}
          key={mapName}
          className={mapName}
          animate={true}
          noWrap={true}
          attributionControl={false}
          center={mapCenter}
          zoom={zoom}
          minZoom={2}
          maxZoom={20}
          zoomControl={false}
          zoomSnap={zoomGranularity}
          zoomDelta={zoomGranularity}
          style={{ width: "100%", height: "100%" }}
        >
          <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />

          {showControls && map && <ZoomControl mapRef={map} mapName={mapName} position={position} />}

          {mapName === "vicinityMap" ? (
            <Marker ref={markerRef} draggable={true} ondragend={handleMarkerDrag} position={position} />
          ) : (
            <>
              {map && <LiveReportImageLayer platform={""} map={map} />}
              <LayerGroup key="site-features">
                {features &&
                  Object.values(features).map((poly) => {
                    if (poly.properties.identity === 0 && !showInactive) return null;
                    if (poly.properties.identity === 1 && !showBoundary) return null;
                    if (poly.properties.identity === 2 && !showExclusion) return null;
                    return (
                      <GeoJSON
                        data={poly}
                        key={poly.properties.index}
                        style={{
                          fillColor: "#ffffff",
                          fillOpacity: 0.01,
                          weight: 1,
                          color: poly.properties.identity === 0 ? "white" : poly.properties.identity === 1 ? "red" : poly.properties.identity === 2 ? "yellow" : "white",
                        }}
                      />
                    );
                  })}
              </LayerGroup>
              <MapLayoutLayer show_racks={show_racks} show_roads={show_roads} show_inverters={show_inverters} liveReportMap={true} />
            </>
          )}
        </MapContainer>
      </section>
    </LiveReportMapWrapper>
  );
};

export { LiveReportMap };
